import React from 'react'
import { Form, Select, Typography } from 'antd'
import { debounce } from 'lodash'
import {
  Coding,
  Extension,
  QuestionnaireItem,
  QuestionnaireResponseItemAnswer
} from '@medplum/fhirtypes'
import {
  QUESTIONNAIRE_API_SEARCH_PARAMS_EXTENSION_URL,
  QUESTIONNAIRE_DATA_FORMAT_EXTENSION_URL,
  QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
} from 'fhir/Questionnaire/constants'
import { useDataFetch } from 'contexts/DataFetchContext'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { getCodeableConceptExtensionValue } from 'fhir/utils'
import { extractDataFromReference, getFormattedDataOptions } from './helpers'
import { HalfWidthContainer, ItemWithTextDetailsContainer } from './styles'
import { useValidateAndUpdateValues } from '../PatAssessment/hooks/useValidateAndUpdateFormValues'

interface DiagnosesDropdownSelectProps {
  question: QuestionnaireItem
  data: Record<string, unknown>
  extensions: Extension[]
  dataReference: Coding
}

export const DiagnosesDropdownSelect = ({
  question,
  data,
  extensions,
  dataReference
}: DiagnosesDropdownSelectProps): JSX.Element => {
  const {
    answers,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()
  const validateAndUpdateFormValues = useValidateAndUpdateValues()

  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  const { Option } = Select

  const defaultItemOptions = extractDataFromReference(data, dataReference)

  const { setSearchQuery, setOffset } = useDataFetch()
  const savedAnswer = question.linkId ? answers[question.linkId] : null

  let initialSelectedValue

  if (savedAnswer) {
    if (Array.isArray(savedAnswer)) {
      initialSelectedValue = savedAnswer.map((answer) => {
        return `${answer.valueCoding?.code.toUpperCase()} - ${answer.valueCoding?.display}`
      })
    } else {
      initialSelectedValue = `${savedAnswer.valueCoding?.code?.toUpperCase()} - ${
        savedAnswer.valueCoding?.display
      }`
    }
  }

  const formatExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_DATA_FORMAT_EXTENSION_URL
  )
  const dataFormat = formatExtension?.coding?.[0]?.code || ''

  const options = defaultItemOptions?.map((item) => {
    const { label } = getFormattedDataOptions(item, dataFormat)
    return { label }
  })

  const formatLabelIntoCodeAndDisplay = (label): Coding => {
    const splitLabel = label.split(' - ')
    const code = splitLabel[0]
    const display = splitLabel[splitLabel.length - 1]
    return {
      code,
      display
    }
  }

  const convertValueToAnswer = (
    value
  ): QuestionnaireResponseItemAnswer | QuestionnaireResponseItemAnswer[] => {
    let newAnswer
    if (Array.isArray(value)) {
      newAnswer = value.map((val) => {
        const codingValue = formatLabelIntoCodeAndDisplay(val)
        return codingValue
      })
    } else {
      const codingValue = formatLabelIntoCodeAndDisplay(value)
      newAnswer = codingValue
    }
    return newAnswer
  }

  const handleSelectChange = (value): void => {
    const newAnswer = convertValueToAnswer(value)
    validateAndUpdateFormValues({
      type: question.type as QuestionnaireAnswerType,
      linkId: question.linkId ?? '',
      text: question.text ?? '',
      value: newAnswer
    })
  }

  const searchParamExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_API_SEARCH_PARAMS_EXTENSION_URL
  )
  const searchParam = searchParamExtension?.coding?.[0]?.code

  const handleSearch = debounce((searchValue): void => {
    if (searchParam) {
      setSearchQuery({ param: searchParam, value: searchValue })
      setOffset(0)
    }
  }, 300)

  const handleScroll = (e): void => {
    if (searchParam) {
      const threshold = 5
      const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + threshold
      if (bottom) {
        setOffset((prevOffset: number) => prevOffset + 50)
      }
    }
  }

  const handleBlur = (): void => {
    if (searchParam) {
      setSearchQuery({ param: searchParam, value: '' })
      setOffset(0)
    }
  }

  const helperTextExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
  )
  const helperText = helperTextExtension?.coding?.[0].display ?? ''

  return (
    <ItemWithTextDetailsContainer
      id={getCssSelectorForLinkId(question.linkId)}
      inQA={inQA}
      inQAEdit={inQAEdit}
      disableHover={disableHover}
      isFocused={question.linkId === focusedCommentLinkId}
      onClick={() => question.linkId && createCommentClickHandler(question.linkId)}
    >
      <HalfWidthContainer>
        <Typography.Title level={5}>{question.text}</Typography.Title>
        {helperText && <Typography.Paragraph type="secondary">{helperText}</Typography.Paragraph>}
      </HalfWidthContainer>
      <HalfWidthContainer>
        <Form.Item
          name={question.linkId}
          initialValue={initialSelectedValue}
          rules={[{ required: question.required, message: 'Please select an option' }]}
        >
          <Select
            mode={question.repeats ? 'multiple' : undefined}
            disabled={question.readOnly || undefined}
            placeholder="Type or select an option"
            style={{ width: '100%' }}
            onSearch={handleSearch}
            onBlur={handleBlur}
            onPopupScroll={handleScroll}
            showSearch={true}
            onChange={handleSelectChange}
          >
            {options?.map((option, index) => (
              <Option key={index} value={option.label}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </HalfWidthContainer>
    </ItemWithTextDetailsContainer>
  )
}
